import { api } from '../api/client';

/**
 * Fetch a server CSV endpoint (which sends raw text/csv via @Res, not the JSON envelope) and save it as
 * a file. Uses `api` (raw axios) with responseType 'text' so the body is the CSV, not an unwrapped object.
 *
 * The saved file must start with a UTF-8 byte-order mark, or Excel opens it as Windows-1252 and every
 * `—` / `₹` / Devanagari character turns to mojibake (`â€”`, `â‚¹`). The server sends one, but decoding
 * the response as text silently drops it — so it is put back here.
 */
const CSV_BOM = String.fromCharCode(0xfeff);

export async function downloadCsv(url: string, filename: string): Promise<void> {
  const r = await api.get(url, { responseType: 'text' });
  const text = String(r.data ?? '');
  const blob = new Blob([text.startsWith(CSV_BOM) ? text : CSV_BOM + text], { type: 'text/csv;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(objectUrl); a.remove(); }, 100);
}

/**
 * Save a BINARY endpoint (xlsx / pdf) to a file.
 *
 * Deliberately separate from downloadCsv: that one reads the body as `text`, which mangles binary
 * bytes through string decoding. The file still "downloads", and Excel then refuses to open it — a
 * corruption you only notice at the point of use. Anything that isn't text/csv must come through here.
 */
export async function downloadBinary(url: string, filename: string, mime: string): Promise<void> {
  const r = await api.get(url, { responseType: 'blob' });
  const blob = new Blob([r.data], { type: mime });
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(objectUrl); a.remove(); }, 100);
}
